// =========================================================================
// A workflow step. Route `/workflows/steps/:stepId`. Twenty-one of them.
// Demo `renderWF`, source line 3696.
//
// The page is three things:
//
//   1. **Hero** — a module chip back to the hub, "Step 2 of 3 in this
//      module", the step name and tag, the lead paragraph, the KPI strip,
//      and a meta row naming the agents that run it and the systems it reads.
//   2. **Body** — `step.blocks(ctx)`, rendered by `WorkflowBlocks`. This is
//      where the step actually lives: cards, tables, bar charts, notes,
//      route panels and customer quotes, all derived from the loaded pack.
//   3. **Also in the platform** — the step's own cross-references, the
//      module's other steps, and a way back to the hub.
//
// `kpis(ctx)` and `blocks(ctx)` are pure functions on the seed; every number
// they print comes from the context `useWorkflowStepContext()` assembles.
// Nothing on this page is typed in, and nothing here calls a clock.
//
// UI layer. Fork-owned (zone C), P5a lane.
// =========================================================================

import { useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import {
  HelpAnchor,
  SkeletonBlock,
  SurfacePageBody,
  SurfacePageLayout,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { AskStrip } from '@/versions/v1/chrome-oem/AskStrip';
import { SceneSection } from '@/versions/v1/shared/oem-tiles';
import { SectionHero } from './SectionHero';
import { SeedGlyph } from './SeedGlyph';
import { useSeedNav } from './use-seed-nav';
import { stepNeedsMaximo, useWorkflowStepContext } from './workflow-context';
import { WorkflowBlocks } from './WorkflowBlocks';
import { WorkflowKpis } from './WorkflowKpis';
import { alsoChips, stepFor, stepFrame } from './WorkflowStepScene.transform';

export function WorkflowStepScene() {
  const { stepId } = useParams<{ stepId: string }>();
  const navigate = useNavigate();

  const step = useMemo(() => stepFor(stepId), [stepId]);
  const frame = useMemo(() => (step ? stepFrame(step) : null), [step]);

  const { ctx, loading, error, maximoPending } = useWorkflowStepContext(
    stepNeedsMaximo(step?.id),
  );

  const seedNav = useSeedNav(
    useMemo(
      (): Readonly<Record<string, readonly Readonly<Record<string, unknown>>[]>> =>
        ctx
          ? {
              wos: ctx.workOrders as unknown as readonly Record<string, unknown>[],
              cases: ctx.pack.cases as unknown as readonly Record<string, unknown>[],
              inv: ctx.pack.inventory as unknown as readonly Record<string, unknown>[],
              parts: ctx.pack.parts as unknown as readonly Record<string, unknown>[],
              orders: ctx.pack.orders as unknown as readonly Record<string, unknown>[],
            }
          : {},
      [ctx],
    ),
  );

  const openMachine = useCallback(
    (id: string) => navigate(`/machines/${encodeURIComponent(id)}`),
    [navigate],
  );

  const actions = useMemo(
    () => ({ dispatch: seedNav.dispatch, openMachine }),
    [seedNav.dispatch, openMachine],
  );

  /**
   * The step's own derivations.
   *
   * `kpis` and `blocks` are the seed's pure functions over the context, so
   * they only run once the context is complete. A step whose body throws on
   * unexpected data would take the page down; it is caught here and reported
   * in place, because one bad table is not a reason to lose the other five.
   */
  const body = useMemo(() => {
    if (!step || !ctx) return null;
    try {
      return { kpis: step.kpis(ctx), blocks: step.blocks(ctx), failed: false as const };
    } catch (e) {
      return {
        kpis: [],
        blocks: [],
        failed: true as const,
        reason: e instanceof Error ? e.message : String(e),
      };
    }
  }, [step, ctx]);

  if (!step || !frame) {
    return (
      <SurfacePageLayout width="full">
        <SurfacePageBody className="max-w-[900px] pt-10">
          <h1 className="text-[22px] font-semibold" style={{ color: 'var(--ink-primary)' }}>
            No such workflow step
          </h1>
          <p className="mt-2 text-[13px]" style={{ color: 'var(--ink-secondary)' }}>
            The operating model has twenty-one steps and{' '}
            <code className="mono">{stepId}</code> is not one of them.
          </p>
          <button
            type="button"
            onClick={() => navigate('/workflows')}
            className="mt-4 text-[13px] font-medium"
            style={{ color: 'var(--accent)' }}
          >
            All ten modules
          </button>
        </SurfacePageBody>
      </SurfacePageLayout>
    );
  }

  const chips = alsoChips(frame);

  return (
    <SurfacePageLayout width="full">
      <Commentable name="WorkflowStepScene">
        <SurfacePageBody className="@container max-w-[1320px] space-y-8 pt-6">
          {error ? (
            <div
              role="alert"
              className="rounded-xl border p-4 text-sm"
              style={{
                borderColor: 'var(--hairline)',
                background: 'var(--crit-soft)',
                color: 'var(--crit)',
              }}
            >
              The aftermarket data could not be loaded ({error.message}). This step is derived
              entirely from that data, so nothing is shown rather than something wrong.
            </div>
          ) : null}

          {/* ---------------------------------------------------------- */}
          {/* Hero                                                       */}
          {/* ---------------------------------------------------------- */}
          <Commentable name="Hero">
            <SectionHero
              colour={frame.module.col}
              title={frame.step.name}
              tag={frame.step.tag}
              lead={frame.step.purpose}
              stats={[]}
              eyebrow={
                <>
                  <button
                    type="button"
                    onClick={() => navigate(`/workflows/modules/${frame.module.id}`)}
                    className="inline-flex items-center gap-[7px] rounded-full border px-2.5 py-1 text-[11px] font-semibold tracking-[0.03em] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]"
                    style={{
                      background: `color-mix(in srgb, ${frame.module.col} 10%, transparent)`,
                      borderColor: `color-mix(in srgb, ${frame.module.col} 27%, transparent)`,
                      color: frame.module.col,
                    }}
                  >
                    <SeedGlyph markup={frame.module.icon} className="h-3.5 w-3.5" />
                    <span>
                      {frame.moduleNumber} {frame.module.name}
                    </span>
                  </button>
                  <span
                    className="text-[10.5px] font-medium uppercase tracking-[0.05em]"
                    style={{ color: 'var(--ink-tertiary)' }}
                  >
                    Step {frame.position} of {frame.siblings.length} in this module
                  </span>
                </>
              }
              meta={
                <>
                  <span
                    className="mr-0.5 text-[9.5px] font-medium uppercase tracking-[0.07em]"
                    style={{ color: 'var(--ink-tertiary)' }}
                  >
                    Run by
                  </span>
                  {frame.module.agents.map((name) => (
                    <button
                      key={name}
                      type="button"
                      onClick={() => navigate(`/agents?agent=${encodeURIComponent(name)}`)}
                      className="rounded-[7px] border bg-[var(--surface-2)] px-2 py-0.5 text-[11px] font-medium leading-[1.2] transition-colors hover:bg-[var(--surface-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]"
                      style={{
                        borderColor: 'var(--hairline)',
                        borderBottom: `2px solid ${frame.module.col}`,
                        color: 'var(--ink-primary)',
                      }}
                    >
                      {name}
                    </button>
                  ))}
                  <span
                    className="ml-2.5 mr-0.5 text-[9.5px] font-medium uppercase tracking-[0.07em]"
                    style={{ color: 'var(--ink-tertiary)' }}
                  >
                    Reads
                  </span>
                  {frame.reads.map((system) => (
                    <button
                      key={system}
                      type="button"
                      onClick={() => navigate('/connectors')}
                      className="rounded-full border px-2 py-0.5 text-[10.5px] transition-colors hover:border-[var(--accent)] hover:text-[var(--accent)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]"
                      style={{
                        borderColor: 'var(--hairline)',
                        background: 'var(--surface-2)',
                        color: 'var(--ink-secondary)',
                      }}
                    >
                      {system}
                    </button>
                  ))}
                </>
              }
              actions={
                <>
                  <span className="flex items-center gap-1.5">
                    <button
                      type="button"
                      disabled={!frame.prev}
                      title={frame.prev?.name ?? 'First step in this module'}
                      onClick={() =>
                        frame.prev && navigate(`/workflows/steps/${frame.prev.id}`)
                      }
                      className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] font-medium transition-colors hover:bg-[var(--surface-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)] disabled:cursor-not-allowed disabled:opacity-40"
                      style={{
                        borderColor: 'var(--hairline-strong)',
                        color: 'var(--ink-secondary)',
                      }}
                    >
                      <ArrowLeft className="h-3 w-3" aria-hidden="true" />
                      Prev
                    </button>
                    <button
                      type="button"
                      disabled={!frame.next}
                      title={frame.next?.name ?? 'Last step in this module'}
                      onClick={() =>
                        frame.next && navigate(`/workflows/steps/${frame.next.id}`)
                      }
                      className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] font-medium transition-colors hover:bg-[var(--surface-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)] disabled:cursor-not-allowed disabled:opacity-40"
                      style={{
                        borderColor: 'var(--hairline-strong)',
                        color: 'var(--ink-secondary)',
                      }}
                    >
                      Next
                      <ArrowRight className="h-3 w-3" aria-hidden="true" />
                    </button>
                  </span>
                  <HelpAnchor doc="workflows/step" />
                </>
              }
            />
          </Commentable>

          {body && !body.failed ? (
            <WorkflowKpis kpis={body.kpis} className="-mt-3" />
          ) : (
            <SkeletonBlock className="h-14 w-full max-w-lg" />
          )}

          <AskStrip view="module" />

          {/* ---------------------------------------------------------- */}
          {/* Body                                                       */}
          {/* ---------------------------------------------------------- */}
          <Commentable name="Blocks">
            <div className="space-y-8">
              {!body ? (
                <>
                  <SkeletonBlock className="h-56 rounded-xl" />
                  <SkeletonBlock className="h-40 rounded-xl" />
                  {maximoPending ? (
                    <p className="sr-only" role="status">
                      Loading the Maximo service register.
                    </p>
                  ) : null}
                </>
              ) : body.failed ? (
                <div
                  role="alert"
                  className="rounded-xl border p-4 text-sm"
                  style={{
                    borderColor: 'var(--hairline)',
                    background: 'var(--crit-soft)',
                    color: 'var(--crit)',
                  }}
                >
                  This step could not be derived from the data pack ({body.reason}).
                </div>
              ) : (
                <WorkflowBlocks
                  blocks={body.blocks}
                  actions={actions}
                  theme={{ colour: frame.module.col }}
                />
              )}
            </div>
          </Commentable>

          {/* ---------------------------------------------------------- */}
          {/* Also in the platform                                       */}
          {/* ---------------------------------------------------------- */}
          <Commentable name="Also">
            <SceneSection
              title="Also in the platform"
              subtitle="the same ground from a different angle — this step is not a copy of any of them"
            >
              <div className="flex flex-wrap gap-[7px]">
                {chips.map((chip) => (
                  <button
                    key={chip.key}
                    type="button"
                    onClick={() => seedNav.dispatch(chip.nav)}
                    className="rounded-md border px-2.5 py-1 text-[11.5px] font-medium transition-colors hover:bg-[var(--surface-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]"
                    style={{
                      borderColor: 'var(--hairline-strong)',
                      background: 'var(--surface-2)',
                      color: 'var(--ink-primary)',
                    }}
                  >
                    {chip.label} ›
                  </button>
                ))}
              </div>
            </SceneSection>
          </Commentable>

          {loading && !body ? (
            <p className="sr-only" role="status">
              Loading the aftermarket data.
            </p>
          ) : null}
        </SurfacePageBody>
      </Commentable>
    </SurfacePageLayout>
  );
}

export default WorkflowStepScene;
